import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CATEGORIES = [
  { value: 'safety_emergency', label: 'Safety / Emergency', description: 'Report a safety concern or emergency' },
  { value: 'bad_actor', label: 'Report Inappropriate Behavior', description: 'Report a rider or driver for inappropriate behavior' },
  { value: 'payment_dispute', label: 'Payment Dispute', description: 'Dispute a charge or request a refund' },
  { value: 'bug_report', label: 'Bug Report', description: 'Report an app bug or technical issue' },
  { value: 'lost_and_found', label: 'Lost & Found', description: 'Report a lost or found item' },
  { value: 'general_feedback', label: 'General Feedback', description: 'Share feedback or suggestions' },
];

const ReportForm = ({ userType, onSuccess, onCancel }) => {
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [rideId, setRideId] = useState('');
  const [dayBookingId, setDayBookingId] = useState('');
  const [reportedUserId, setReportedUserId] = useState('');
  const [recentRides, setRecentRides] = useState([]);
  const [loadingRides, setLoadingRides] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentRides();
  }, []);

  const fetchRecentRides = async () => {
    try {
      setLoadingRides(true);
      const token = localStorage.getItem('token');
      const endpoint = userType === 'driver'
        ? `${process.env.REACT_APP_API_URL}/api/drivers/rides/history`
        : `${process.env.REACT_APP_API_URL}/api/riders/rides/history`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const rides = Array.isArray(response.data) ? response.data : (response.data?.rides || []);
      setRecentRides(rides.slice(0, 20));
    } catch (err) {
      console.error('Error fetching rides for report form:', err);
    } finally {
      setLoadingRides(false);
    }
  };

  const handleRideSelect = (e) => {
    const value = e.target.value;
    setRideId('');
    setDayBookingId('');
    setReportedUserId('');

    if (!value) return;

    const selected = recentRides.find(r => {
      const id = r.is_day_booking ? `db-${r.booking_id}` : `ride-${r.id}`;
      return id === value;
    });

    if (selected) {
      if (selected.is_day_booking) {
        setDayBookingId(selected.booking_id);
      } else {
        setRideId(selected.id);
      }

      // Auto-populate reported user for bad_actor category
      if (category === 'bad_actor' && selected) {
        if (userType === 'rider' && selected.driver_user_id) {
          setReportedUserId(selected.driver_user_id);
        } else if (userType === 'driver' && selected.rider_user_id) {
          setReportedUserId(selected.rider_user_id);
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!category) {
      setError('Please select a category');
      return;
    }
    if (subject.length < 3) {
      setError('Subject must be at least 3 characters');
      return;
    }
    if (description.length < 10) {
      setError('Description must be at least 10 characters');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const payload = {
        category,
        subject,
        description,
      };

      if (rideId) payload.ride_id = parseInt(rideId);
      if (dayBookingId) payload.day_booking_id = parseInt(dayBookingId);
      if (reportedUserId && category === 'bad_actor') payload.reported_user_id = parseInt(reportedUserId);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reports`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmittedId(response.data.id);
      setSubmitted(true);
      if (onSuccess) onSuccess(response.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit report. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h3 className="text-lg font-semibold text-green-800 mb-2">Report Submitted</h3>
        <p className="text-green-700 mb-1">Your report <strong>ID#{submittedId}</strong> has been received.</p>
        <p className="text-green-600 text-sm">Our team will review it and follow up if needed. You can track the status in "My Reports" below.</p>
        <button
          onClick={() => {
            setSubmitted(false);
            setCategory('');
            setSubject('');
            setDescription('');
            setRideId('');
            setDayBookingId('');
            setReportedUserId('');
            setError(null);
          }}
          className="mt-4 text-green-700 hover:text-green-900 font-medium text-sm underline"
        >
          Submit Another Report
        </button>
      </div>
    );
  }

  const getRideLabel = (ride) => {
    const formatLocalDateTime = (isoString) => {
      if (!isoString) return { date: 'Unknown date', time: '' };
      const d = new Date(isoString);
      const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
      return { date, time };
    };

    if (ride.is_day_booking) {
      const dateTimeSource = ride.pickup_datetime || ride.first_pickup_time || ride.created_at;
      const { date, time } = formatLocalDateTime(dateTimeSource);
      const rateLabel = ride.rate_type
        ? ride.rate_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : 'N/A';
      return `Day Booking (${rateLabel}) - ${date}${time ? ` - ${time}` : ''}`;
    }

    const dateTimeSource = ride.pickup_datetime || ride.pickup_time;
    const { date, time } = formatLocalDateTime(dateTimeSource);
    return `Individual Ride - ${date}${time ? ` - ${time}` : ''}`;
  };

  const getRideValue = (ride) => {
    return ride.is_day_booking ? `db-${ride.booking_id}` : `ride-${ride.id}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select a category...</option>
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        {category && (
          <p className="mt-1 text-xs text-gray-500">
            {CATEGORIES.find(c => c.value === category)?.description}
          </p>
        )}
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief summary of your report"
          maxLength={255}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="mt-1 text-xs text-gray-400">{subject.length}/255</p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Please provide as much detail as possible..."
          maxLength={5000}
          rows={5}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
          required
        />
        <p className="mt-1 text-xs text-gray-400">{description.length}/5000</p>
      </div>

      {/* Related Ride/Booking */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Related Ride (optional)</label>
        {loadingRides ? (
          <p className="text-sm text-gray-500">Loading your recent rides...</p>
        ) : recentRides.length > 0 ? (
          <select
            value={rideId ? `ride-${rideId}` : (dayBookingId ? `db-${dayBookingId}` : '')}
            onChange={handleRideSelect}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">None - not related to a specific ride</option>
            {recentRides.map(ride => (
              <option key={getRideValue(ride)} value={getRideValue(ride)}>
                {getRideLabel(ride)}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-gray-500">No recent rides found</p>
        )}
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {submitting ? 'Submitting...' : 'Submit Report'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default ReportForm;
